import { 
  collection, 
  getDocs, 
  query, 
  where,
  doc,
  updateDoc,
  writeBatch,
  arrayRemove,
  arrayUnion
} from 'firebase/firestore';
import { db } from './firebase';
import { Logo, ProjectName } from '../types';

const LOGOS_COLLECTION = 'logos';
const PROJECT_NAMES_COLLECTION = 'projectNames';

export interface TagWithCounts {
  tag: string;
  logoCount: number;
  projectNameCount: number;
  totalCount: number;
}

// Helper function to check if two tags are similar
function isSimilar(tag1: string, tag2: string): boolean {
  const normalize = (str: string) => str.toLowerCase().trim();
  const norm1 = normalize(tag1);
  const norm2 = normalize(tag2);

  // Exact match after normalization
  if (norm1 === norm2) return true;

  // One is a substring of the other
  if (norm1.includes(norm2) || norm2.includes(norm1)) return true;

  // Similar with common variations (plurals, etc.)
  const variations = [
    [norm1, norm1 + 's'],
    [norm1, norm1 + 'es'],
    [norm1, norm1 + 'ing'],
    [norm2, norm2 + 's'],
    [norm2, norm2 + 'es'],
    [norm2, norm2 + 'ing']
  ];

  for (const [base, variant] of variations) {
    if (base === normalize(tag2) || variant === normalize(tag2)) return true;
    if (base === normalize(tag1) || variant === normalize(tag1)) return true;
  }

  // Levenshtein distance for typos (simple version)
  if (levenshteinDistance(norm1, norm2) <= 2 && Math.min(norm1.length, norm2.length) >= 4) {
    return true;
  }

  return false;
}

// Simple Levenshtein distance calculation
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[len2][len1];
}

export const tagService = {
  // Get all tags with usage counts across modules
  async getAllTagsWithCounts(): Promise<TagWithCounts[]> {
    try {
      const tagCounts = new Map<string, { logoCount: number; projectNameCount: number }>();

      // Get logos tags
      const logosQuery = query(collection(db, LOGOS_COLLECTION), where('deleted', '==', false));
      const logosSnapshot = await getDocs(logosQuery);
      
      logosSnapshot.docs.forEach(doc => {
        const logo = doc.data() as Logo;
        logo.tags?.forEach((tag: string) => {
          if (tag.trim()) {
            const current = tagCounts.get(tag) || { logoCount: 0, projectNameCount: 0 };
            tagCounts.set(tag, { ...current, logoCount: current.logoCount + 1 });
          }
        });
      });

      // Get project names tags
      const projectNamesQuery = query(collection(db, PROJECT_NAMES_COLLECTION), where('deleted', '==', false));
      const projectNamesSnapshot = await getDocs(projectNamesQuery);
      
      projectNamesSnapshot.docs.forEach(doc => {
        const projectName = doc.data() as ProjectName;
        projectName.tags?.forEach((tag: string) => {
          if (tag.trim()) {
            const current = tagCounts.get(tag) || { logoCount: 0, projectNameCount: 0 };
            tagCounts.set(tag, { ...current, projectNameCount: current.projectNameCount + 1 });
          }
        });
      });

      // Convert to array and sort by total usage
      return Array.from(tagCounts.entries())
        .map(([tag, counts]) => ({
          tag,
          logoCount: counts.logoCount,
          projectNameCount: counts.projectNameCount,
          totalCount: counts.logoCount + counts.projectNameCount
        }))
        .sort((a, b) => b.totalCount - a.totalCount);
    } catch (error) {
      console.error('Error fetching tags with counts:', error);
      throw error;
    }
  },

  // Replace a tag across all modules
  async replaceTag(oldTag: string, newTag: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    const batch = writeBatch(db);
    const errors: string[] = [];
    let updated = 0;

    try {
      // Update logos
      const logosQuery = query(
        collection(db, LOGOS_COLLECTION), 
        where('tags', 'array-contains', oldTag),
        where('deleted', '==', false)
      );
      const logosSnapshot = await getDocs(logosQuery);
      
      logosSnapshot.docs.forEach(doc => {
        const logoRef = doc.ref;
        batch.update(logoRef, {
          tags: arrayRemove(oldTag)
        });
        
        // Only add new tag if it's different and not empty
        if (newTag.trim() && newTag !== oldTag) {
          batch.update(logoRef, {
            tags: arrayUnion(newTag.trim())
          });
        }
        updated++;
      });

      // Update project names
      const projectNamesQuery = query(
        collection(db, PROJECT_NAMES_COLLECTION), 
        where('tags', 'array-contains', oldTag),
        where('deleted', '==', false)
      );
      const projectNamesSnapshot = await getDocs(projectNamesQuery);
      
      projectNamesSnapshot.docs.forEach(doc => {
        const projectNameRef = doc.ref;
        batch.update(projectNameRef, {
          tags: arrayRemove(oldTag)
        });
        
        // Only add new tag if it's different and not empty
        if (newTag.trim() && newTag !== oldTag) {
          batch.update(projectNameRef, {
            tags: arrayUnion(newTag.trim())
          });
        }
        updated++;
      });

      // Commit all updates
      await batch.commit();

      return { updated, errors };
    } catch (error) {
      console.error('Error replacing tag:', error);
      throw error;
    }
  },

  // Delete a tag (remove from all modules)
  async deleteTag(tagToDelete: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    return this.replaceTag(tagToDelete, '', updatedBy);
  },

  // Merge multiple tags into one
  async mergeTags(tagsToMerge: string[], targetTag: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    let totalUpdated = 0;
    const allErrors: string[] = [];

    try {
      for (const tag of tagsToMerge) {
        if (tag !== targetTag) {
          const result = await this.replaceTag(tag, targetTag, updatedBy);
          totalUpdated += result.updated;
          allErrors.push(...result.errors);
        }
      }

      return { updated: totalUpdated, errors: allErrors };
    } catch (error) {
      console.error('Error merging tags:', error);
      throw error;
    }
  },

  // Get similar tags (for suggesting merges)
  async findSimilarTags(tags: TagWithCounts[]): Promise<Array<{ baseTag: string; similarTags: string[] }>> {
    const similarGroups: Array<{ baseTag: string; similarTags: string[] }> = [];
    const processed = new Set<string>();

    tags.forEach(({ tag }) => {
      if (processed.has(tag)) return;

      const similar = tags
        .filter(({ tag: otherTag }) => 
          otherTag !== tag && 
          !processed.has(otherTag) &&
          isSimilar(tag, otherTag)
        )
        .map(({ tag: similarTag }) => {
          processed.add(similarTag);
          return similarTag;
        });

      if (similar.length > 0) {
        similarGroups.push({
          baseTag: tag,
          similarTags: similar
        });
        processed.add(tag);
      }
    });

    return similarGroups;
  }
};